import { Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { EnrichedWorkspace, FormattedUsageDataPoint, UsageBreakdownItem, UsageWithBreakdown, User } from '../types';

/**
 * Converts milli AI units to AI units
 */
function convertMilliAiUnitsToAiUnits(milliAiUnits: string | undefined): number {
  if (!milliAiUnits) return 0;
  const milliUnits = parseInt(milliAiUnits);
  return milliUnits * 0.001; // Convert from milli AI units to AI units
}

/**
 * Resolves a group ID to a human-readable display name based on the grouping type
 *
 * @param groupId The group identifier (UUID or string)
 * @param groupBy The grouping type (WORKSPACE, USER, COMPONENT, TAG)
 * @param workspacesMap Map of workspace UUIDs to workspace objects
 * @param usersMap Map of user UUIDs to user objects
 * @returns Human-readable display name
 */
export function resolveGroupDisplayName(
  groupId: string,
  groupBy: Grouping,
  workspacesMap: Map<string, EnrichedWorkspace>,
  usersMap: Map<string, User>
): string {
  if (groupBy === Grouping.WORKSPACE) {
    const workspace = workspacesMap.get(`workspaces/${groupId}`);
    if (workspace) {
      if (workspace.displayName === 'Personal Workspace') {
        // Personal workspace: "PW - {userDisplayName}"
        return `PW - ${workspace.userDisplayName}`;
      } else {
        // Non-personal workspace: use workspace displayName
        return workspace.displayName;
      }
    }
  } else if (groupBy === Grouping.USER) {
    // For user grouping, lookup user by "users/{uuid}"
    const user = usersMap.get(`users/${groupId}`);
    if (user) {
      return user.displayName || groupId;
    }
  }
  // For other grouping types (COMPONENT, TAG), keep the original groupId
  return groupId;
}

/**
 * Formats raw usage data into a structure suitable for chart visualization
 *
 * Transforms the API response into data points with time and group-based values,
 * replacing UUIDs with human-readable names based on the grouping type.
 * Merges duplicate time entries by summing their values.
 *
 * @param usageData Raw usage data from the API
 * @param groupBy The grouping type
 * @param workspacesMap Map of workspace UUIDs to workspace objects
 * @param usersMap Map of user UUIDs to user objects
 * @param formatDate Function to format dates based on granularity
 * @returns Object containing formatted data points and unique groups
 */
export function formatUsageDataForChart(
  usageData: UsageWithBreakdown[],
  groupBy: Grouping,
  workspacesMap: Map<string, EnrichedWorkspace>,
  usersMap: Map<string, User>,
  formatDate: (date: Date) => string
): { formattedData: FormattedUsageDataPoint[]; groups: string[] } {
  // Collect all unique groups across all data points
  const allGroups = new Set<string>();

  // Use a Map to merge data points with the same formatted time
  const dataByTime = new Map<string, FormattedUsageDataPoint>();

  usageData.forEach((datum) => {
    const usageBreakdown = datum.usageBreakdown || [];
    const time = (datum.interval?.startTime && formatDate(new Date(datum.interval.startTime))) || '';

    // Get or create data point for this time
    if (!dataByTime.has(time)) {
      dataByTime.set(time, { time });
    }
    const dataPoint = dataByTime.get(time)!;

    // Add/sum each group value
    usageBreakdown.forEach((breakdown: UsageBreakdownItem) => {
      const groupId = breakdown.group || 'unknown';
      const milliAiUnits = breakdown.usage?.milliAiUnits;
      const aiUnits = convertMilliAiUnitsToAiUnits(milliAiUnits);

      // Replace UUID with readable name based on grouping type
      const displayName = resolveGroupDisplayName(groupId, groupBy, workspacesMap, usersMap);

      // Sum if already exists, otherwise set
      const existingValue = dataPoint[displayName];
      dataPoint[displayName] = (typeof existingValue === 'number' ? existingValue : 0) + aiUnits;
      allGroups.add(displayName);
    });
  });

  // Convert map to array, preserving insertion order
  const formattedData = Array.from(dataByTime.values());

  return {
    formattedData,
    groups: Array.from(allGroups),
  };
}
